import PropTypes from 'prop-types'
import { Box, ButtonBase, Card, Divider, Grid, Typography } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useGetIdentity } from 'react-admin'
import CardMetric from './Components/CardMetric'
import FitnessStatTile from './Components/FitnessStatTile'
import HourHistory from './Components/HourHistory'
import {
  FITNESS_METRICS,
  TRACKED_METRICS,
  formatTimeRemaining,
} from './Utils/utils'
import FitnessRings from './Components/FitnessRings'
import FitnessRingsIcon from './Components/FitnessRingsIcon'
import DailySummary from './Components/DailySummary'
import { useFitnessTrackerContext } from './Context/FitnessTrackerContext'
import { useTimeRemaining } from './Hooks/useTimeRemaining'

const CardHeading = ({ label }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="h5"
        fontWeight="bold"
        color="text.secondary"
        textTransform="uppercase"
      >
        {label}
      </Typography>
    </Box>
  )
}

CardHeading.propTypes = {
  label: PropTypes.string,
}

export default function ExpandedDashboard() {
  const { stats, currentHour, historyHours, setExpanded } =
    useFitnessTrackerContext()
  const timeRemaining = useTimeRemaining()
  const { data: identity } = useGetIdentity()

  const calculateCVR = (booked, leads) =>
    leads > 0 ? Number(((booked / leads) * 100).toFixed(1)) : 0

  const actualCVR = calculateCVR(
    stats.daily.scrapBooked.value + stats.daily.salvageBooked.value,
    stats.daily.newLeads.value
  )

  const targetCVR = calculateCVR(
    stats.daily.scrapBooked.target + stats.daily.salvageBooked.target,
    stats.daily.newLeads.target
  )

  const summaryItems = [
    {
      key: 'cvr',
      label: 'CVR',
      data: {
        value: actualCVR,
        target: targetCVR,
      },
    },

    ...Object.entries(FITNESS_METRICS).map(([key, { label }]) => ({
      key,
      label,
      data: stats.daily[key],
    })),
  ]

  return (
    <Card
      sx={{
        width: '100%',
        p: 3,
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderRadius: 4,
      }}
    >
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <FitnessRingsIcon fontSize="large" />
          <Typography
            variant="h5"
            fontWeight="bold"
            color="text.secondary"
            textTransform="uppercase"
          >
            Performance
          </Typography>
          <Typography color="text.secondary" lineHeight="1">
            {identity.fullName}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Typography variant="body2">
            Resets in {formatTimeRemaining(timeRemaining)}
          </Typography>
          <ButtonBase
            onClick={() => setExpanded(false)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            Collapse
            <ExpandLessIcon fontSize="small" />
          </ButtonBase>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CardHeading label="Daily Totals" />
          <Card
            sx={{
              p: 2,
              bgcolor: 'action.hover',
              color: 'text.primary',
              borderRadius: 3,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
              }}
            >
              {summaryItems.map(({ key, label, data }) => (
                <DailySummary key={key} label={label} data={data} />
              ))}
            </Box>
          </Card>
          {Object.entries(TRACKED_METRICS).map(
            ([key, { label, colour, icon }]) => (
              <CardMetric
                key={key}
                label={label}
                data={stats.daily[key]}
                colour={colour}
                icon={icon}
              />
            )
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <CardHeading label="Hourly Goals" />
          <Box display="flex" justifyContent="center" mb={1}>
            <Box sx={{ maxWidth: '245px' }}>
              <FitnessRings stats={currentHour} />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
            }}
          >
            {Object.entries(TRACKED_METRICS).map(([key, { label, colour }]) => (
              <FitnessStatTile
                key={key}
                label={label}
                data={currentHour[key]}
                colour={colour}
              />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ maxWidth: '425px' }}>
            <HourHistory hours={historyHours} />
          </Box>
        </Grid>
      </Grid>
    </Card>
  )
}
